import React, { useState, useEffect } from 'react';
import { Plus, Key, TrendingUp, AlertCircle, X } from 'lucide-react';
import Button from '../../components/ui/Button';
import { GenerateKeyModal } from './components/GenerateKeyModal';
import { ApiKeyCard } from './components/ApiKeyCard';
import { UsageAnalytics } from './components/UsageAnalytics';
import { apiKeyService } from '../../services/apiKeyService';
import { useAuth } from '../../contexts/AuthContext';

export default function ApiKeyManagement() {
  const { userProfile } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedKeyForUsage, setSelectedKeyForUsage] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [usageLogs, setUsageLogs] = useState([]);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Extract artist brand colors — only applied when the logged-in user is an artist
  const isArtist = userProfile?.role === 'artist';
  const highlightColor = isArtist
    ? (userProfile?.highlight_color || userProfile?.highlightColor || null)
    : null;
  const secondaryColor = isArtist
    ? (userProfile?.secondary_color || userProfile?.secondaryColor || highlightColor || null)
    : null;

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError('');
      const keys = await apiKeyService?.getApiKeys();
      setApiKeys(keys);
    } catch (err) {
      setError(err?.message || 'Failed to load API keys');
      console.error('Error loading API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async (keyData) => {
    try {
      const newKey = await apiKeyService?.generateApiKey(keyData);
      setApiKeys(prev => [newKey, ...prev]);
      setShowGenerateModal(false);
    } catch (err) {
      console.error('Error generating API key:', err);
      throw err;
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiKeyService?.revokeApiKey(keyId);
      await loadApiKeys();
    } catch (err) {
      setError(err?.message || 'Failed to revoke API key');
      console.error('Error revoking API key:', err);
    }
  };

  const handleRegenerateKey = async (keyId) => {
    if (!confirm('Regenerating will revoke the current key and create a new one. Continue?')) {
      return;
    }

    try {
      const newKey = await apiKeyService?.regenerateApiKey(keyId);
      await loadApiKeys();
      alert('API key regenerated successfully. Please update your applications with the new credentials.');
    } catch (err) {
      setError(err?.message || 'Failed to regenerate API key');
      console.error('Error regenerating API key:', err);
    }
  };

  const handleViewUsage = async (apiKey) => {
    try {
      setLoadingUsage(true);
      setSelectedKeyForUsage(apiKey);
      
      const [stats, logs] = await Promise.all([
        apiKeyService?.getUsageStatistics(apiKey?.id),
        apiKeyService?.getUsageLogs(apiKey?.id, 50, 0)
      ]);
      
      setUsageStats(stats);
      setUsageLogs(logs);
    } catch (err) {
      setError(err?.message || 'Failed to load usage data');
      console.error('Error loading usage data:', err);
    } finally {
      setLoadingUsage(false);
    }
  };

  const closeUsageView = () => {
    setSelectedKeyForUsage(null);
    setUsageStats(null);
    setUsageLogs([]);
  };

  const activeKeysCount = apiKeys?.filter(key => key?.status === 'active')?.length;
  const totalUsage = apiKeys?.reduce((sum, key) => sum + (key?.currentUsage || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderColor: highlightColor || '#2563EB' }}
          ></div>
          <p className="text-gray-600">Loading API keys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Artist brand color header accent strip */}
      {highlightColor && (
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(to right, ${highlightColor}, ${secondaryColor || highlightColor}, transparent)` }}
        />
      )}

      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center space-x-3 mb-1">
                  {highlightColor && (
                    <div
                      className="w-1 h-8 rounded-full"
                      style={{ background: `linear-gradient(to bottom, ${highlightColor}, ${secondaryColor || highlightColor})` }}
                    />
                  )}
                  <h1 className="text-3xl font-bold text-gray-900">API Key Management</h1>
                </div>
                <p className="text-gray-600 mt-2">
                  Generate and manage API keys for your mobile app integrations
                </p>
              </div>
              <button
                onClick={() => setShowGenerateModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors text-white"
                style={highlightColor ? {
                  background: `linear-gradient(135deg, ${highlightColor}E6, ${secondaryColor || highlightColor}CC)`,
                  boxShadow: `0 2px 8px ${highlightColor}40`
                } : { backgroundColor: '#2563EB' }}
              >
                <Plus className="w-5 h-5" />
                Generate New Key
              </button>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Active Keys</p>
                    <p className="text-3xl font-bold text-gray-900">{activeKeysCount}</p>
                  </div>
                  <div
                    className="p-3 rounded-lg"
                    style={highlightColor ? { backgroundColor: `${highlightColor}20` } : { backgroundColor: '#DBEAFE' }}
                  >
                    <Key
                      className="w-8 h-8"
                      style={highlightColor ? { color: highlightColor } : { color: '#2563EB' }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total API Calls</p>
                    <p className="text-3xl font-bold text-gray-900">{totalUsage?.toLocaleString()}</p>
                  </div>
                  <div
                    className="p-3 rounded-lg"
                    style={highlightColor ? { backgroundColor: `${secondaryColor || highlightColor}20` } : { backgroundColor: '#DCFCE7' }}
                  >
                    <TrendingUp
                      className="w-8 h-8"
                      style={highlightColor ? { color: secondaryColor || highlightColor } : { color: '#16A34A' }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Keys</p>
                    <p className="text-3xl font-bold text-gray-900">{apiKeys?.length}</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Key className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-700">{error}</p>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Usage Analytics View */}
          {selectedKeyForUsage && (
            <div className="mb-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Usage Analytics</h2>
                    <p className="text-gray-600 mt-1">{selectedKeyForUsage?.keyName}</p>
                  </div>
                  <Button onClick={closeUsageView} variant="secondary">
                    Close
                  </Button>
                </div>

                {loadingUsage ? (
                  <div className="text-center py-12">
                    <div
                      className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                      style={{ borderColor: highlightColor || '#2563EB' }}
                    ></div>
                    <p className="text-gray-600">Loading usage data...</p>
                  </div>
                ) : (
                  <UsageAnalytics statistics={usageStats || {}} logs={usageLogs} />
                )}
              </div>
            </div>
          )}

          {/* API Keys List */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your API Keys</h2>
            
            {apiKeys?.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <Key className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No API Keys Yet</h3>
                <p className="text-gray-600 mb-6">
                  Generate your first API key to start integrating with your mobile applications
                </p>
                <Button onClick={() => setShowGenerateModal(true)}>
                  Generate Your First Key
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {apiKeys?.map((apiKey) => (
                  <ApiKeyCard
                    key={apiKey?.id}
                    apiKey={apiKey}
                    onRevoke={handleRevokeKey}
                    onRegenerate={handleRegenerateKey}
                    onViewUsage={handleViewUsage}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Documentation Link */}
          <div
            className="mt-8 rounded-lg p-6 border"
            style={highlightColor ? {
              backgroundColor: `${highlightColor}10`,
              borderColor: `${highlightColor}30`
            } : { backgroundColor: '#EFF6FF', borderColor: '#BFDBFE' }}
          >
            <h3
              className="text-lg font-semibold mb-2"
              style={highlightColor ? { color: highlightColor } : { color: '#1E3A5F' }}
            >Integration Documentation</h3>
            <p className="text-blue-700 mb-4">
              Learn how to integrate these API keys into your mobile applications
            </p>
            <a
              href="/MOBILE_API_DOCUMENTATION.md"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 font-medium underline"
            >
              View Mobile API Documentation →
            </a>
          </div>
        </div>
      </div>
      {/* Generate Key Modal */}
      <GenerateKeyModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleGenerateKey}
      />
    </div>
  );
}